import { type FC, type ReactNode } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import CheckShield from 'dibs-icons/exports/CheckShield.ts';
import { useBuyerIncentivesMessages } from './hooks/useBuyerIncentivesMessages.tsx';
import classnames from 'classnames';
import dibsCss from 'dibs-css';

export const Incentives: FC = () => {
    const incentives = useBuyerIncentivesMessages();

    return (
        <>
            {incentives.map((incentive, index) => (
                <div key={index} className={classnames(dibsCss.inline, dibsCss.whitespacePreWrap)}>
                    <FormattedMessage
                        id="PdpSharedSidebarBuyerIncentives.incentive"
                        defaultMessage="{isLast, select, true {{incentive}} other {{incentive}, }}"
                        values={{ incentive, isLast: index === incentives.length - 1 }}
                    />
                </div>
            ))}
        </>
    );
};

export type BuyerIncentivesBannerVariant = 'control' | 'variant' | 'variant-b';

const getDetailsLink = (getLink: (chunk: ReactNode) => ReactNode): ReactNode =>
    getLink(<FormattedMessage id="BuyerIncentivesBanner.detailsLink" defaultMessage="Details" />);

type Props = {
    getLink: (chunk: ReactNode) => ReactNode;
    linkPosition?: 'top' | 'bottom';
    variant?: BuyerIncentivesBannerVariant;
    titleOverride?: ReactNode;
    descriptionOverride?: ReactNode;
    isMobile?: boolean;
};

export const BuyerIncentivesBanner: FC<Props> = ({
    getLink,
    linkPosition = 'bottom',
    variant = 'control',
    titleOverride,
    descriptionOverride,
    isMobile = false,
}) => {
    const isVariantB = variant === 'variant-b';
    const useCustomCopy = variant === 'variant' || variant === 'variant-b';
    const detailsLink = getDetailsLink(getLink);

    const title = titleOverride ?? (
        <FormattedMessage
            id="BuyerIncentivesBanner.incentives.title"
            defaultMessage="Shop With Confidence {showLink, select, true {{detailsLink}} other {}}"
            values={{
                showLink: linkPosition === 'top',
                detailsLink,
            }}
        />
    );

    const description =
        useCustomCopy && descriptionOverride ? (
            <FormattedMessage
                id="BuyerIncentivesBanner.incentives.descriptionWithLink"
                defaultMessage="{description} {detailsLink}"
                values={{
                    description: descriptionOverride,
                    detailsLink,
                }}
            />
        ) : (
            <FormattedMessage
                id="BuyerIncentivesBanner.incentives.message"
                defaultMessage="{incentives} {showLink, select, true {{detailsLink}} other {}}"
                values={{
                    incentives: <Incentives />,
                    showLink: linkPosition === 'bottom',
                    detailsLink,
                }}
            />
        );

    if (isVariantB) {
        return (
            <div
                className={classnames(
                    isMobile ? dibsCss.pXsmall : dibsCss.pSmaller,
                    dibsCss.roundedXxsmall,
                    dibsCss.flex,
                    dibsCss.gapXsmall,
                    dibsCss.itemsStart,
                    dibsCss.bgCyan100
                )}
            >
                <span className={classnames(dibsCss.flex, dibsCss.fillShagreen800)}>
                    <CheckShield variation={isMobile ? '24' : '40'} />
                </span>
                <div>
                    <div
                        className={classnames(dibsCss.sassyFontBodyMediumHeavy, {
                            [dibsCss.mbXxsmall]: !isMobile,
                        })}
                    >
                        {title}
                    </div>
                    <span className={dibsCss.sassyFontBodySmall}>{description}</span>
                </div>
            </div>
        );
    }

    return (
        <div className={classnames(dibsCss.pSmaller, dibsCss.bgShagreen50, dibsCss.roundedXxsmall)}>
            <div
                className={classnames(
                    dibsCss.flex,
                    dibsCss.gapXsmall,
                    dibsCss.sassyFontBodySmallHeavy,
                    dibsCss.mbXxsmall
                )}
            >
                <CheckShield variation="20" />
                {title}
            </div>
            <span className={dibsCss.sassyFontBodySmall}>{description}</span>
        </div>
    );
};
